// Reminders (Kareem, 4 Nov: "the design is not nice"), after SReminders and SPhoneReminders. On desktop there is no AI
// box on the page (Kareem, 5 Nov): plain words go through the shell's Ask AI; the phone keeps its bar (it goes to the
// assistant panel, which proposes a card to approve). Or add one yourself with Add
// reminder (a form: what, when, for whom, linked to; Speak fills it). Below, yours in Overdue, Today, Tomorrow, Later
// and Done, a tick each (6.3: Upcoming split in two by Riyadh dates, lib/reminder-groups.ts; Later says each one's day,
// "Sun 8 Nov 09:00"); beside them, the ones you set for others and how reminders reach you. A phone gets the same in
// its own frame: the bar, Add reminder in a sheet, and the groups as grouped lists.
import { AddButton } from '../ui/AddButton';
import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import type { Reminder, SessionUser } from '@dpw/shared';
import { api } from '../api';
import { useChange, useReminders } from '../lib/queries';
import { usePhone } from '../lib/media';
import { dayLabel, localDay, localTime, relDay } from '../lib/format';
import { groupReminders } from '../lib/reminder-groups';
import { useAssistantPanel } from '../assistant/Panel';
import { AddReminderForm } from '../ui/AddReminderForm';
import { Loading } from '../ui/bits';
import { IconCheck } from '../ui/icons';
import { Modal } from '../ui/Modal';
import { P, PSheet, Title, TopBar } from '../phone/kit';

/** Plain words that work as they are (each becomes a card to approve). */
const TRIES = ['Remind me Sunday at 9 to print the steering deck', 'Remind Sara Monday to send the matrix', 'Remind me tomorrow morning to chase IT for the data dictionary'];

function whenLabel(r: Reminder) {
  const day = r.remindDate ?? localDay(r.remindAt!);
  const rel = relDay(day);
  return `${rel === 'Today' || rel === 'Tomorrow' || rel === 'Yesterday' ? rel : dayLabel(day)}${r.remindAt ? ` ${localTime(r.remindAt)}` : ''}`;
}

/** Who set it and what it is for, in one line ("Set by Sara for you · for: Steering committee"). */
function subLine(r: Reminder, mine: boolean) {
  const ai = r.setBy === 'assistant';
  const setter = r.setByUser?.name.split(' ')[0] ?? 'someone';
  return {
    who: !mine ? `For ${r.forUser.name}${ai ? ' (via the assistant)' : ''}`
      : r.setByOther ? `Set by ${setter} for you${ai ? ' (via the assistant)' : ''}`
      : ai ? 'Set by the assistant for you' : null,
    forWhat: r.activity ? `for: ${r.activity.title}${r.activity.dueDate ? `, due ${dayLabel(r.activity.dueDate)}` : ''}` : null,
  };
}

function useGroups() {
  const list = useReminders('mine');
  const groups = useMemo(() => groupReminders(list.data ?? []), [list.data]);
  return { list, groups };
}

/** The bar at the top: plain words go to the assistant panel (a card to approve); the mic opens it in voice mode. */
function SayBar({ phone }: { phone: boolean }) {
  const panel = useAssistantPanel();
  const [text, setText] = useState('');
  const send = (t: string) => { if (t.trim()) { panel.open({ prompt: t.trim(), send: true, fresh: true }); setText(''); } };
  const submit = (e: FormEvent) => { e.preventDefault(); send(text); };
  const voice = () => panel.open({ voice: true, fresh: true });
  if (phone) {
    return (
      <form className="ph-abar" onSubmit={submit}>
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Remind me Sunday 9am to bring the form" aria-label="Tell the assistant about a reminder" enterKeyHint="send" />
        <button type="button" className="mic" aria-label="Talk it through" onClick={voice}><P.mic /></button>
        <button className="go" aria-label="Send" disabled={!text.trim()}><P.send /></button>
      </form>
    );
  }
  return (
    <>
      <form className="rsay" onSubmit={submit}>
        <span className="aiorb" aria-hidden="true" />
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Remind me on Sunday to ...  ·  Remind Sara Monday to send the matrix" aria-label="Tell the assistant about a reminder" />
        <button type="button" className="ib" aria-label="Talk it through" title="Voice mode, in the assistant panel" onClick={voice}><IconMic /></button>
        <button className="bt pri" disabled={!text.trim()}>Send</button>
      </form>
      <div className="rtry"><span className="xs mut">Try:</span>{TRIES.map(t => <button key={t} type="button" className="pl" onClick={() => send(t)}>{t}</button>)}</div>
    </>
  );
}

function Row({ r, mine }: { r: Reminder; mine: boolean }) {
  const tick = useChange(() => (r.doneAt ? api.reminders.undo(r.id) : api.reminders.done(r.id)));
  const del = useChange(() => api.reminders.remove(r.id));
  const ai = r.setBy === 'assistant';
  const { who, forWhat } = subLine(r, mine);
  return (
    <div className={`rm${ai ? ' ai' : ''}${r.doneAt ? ' done' : ''}`}>
      {mine && <button className={`chk${r.doneAt ? ' on' : ''}`} aria-label={r.doneAt ? 'Not done' : 'Done'} onClick={() => tick.mutate()} disabled={tick.isPending}><IconCheck /></button>}
      <div style={{ minWidth: 0 }}>
        <div className="tx">{r.text}</div>
        {(who || forWhat) && <div className="xs mut lk">{ai && <span className="aiorb" style={{ width: 8, height: 8, marginRight: 6 }} />}{who}{who && forWhat ? ' · ' : ''}{forWhat && r.activity && <Link to={`/activities/${r.activity.id}`}>{forWhat}</Link>}</div>}
      </div>
      <div className="when">
        {r.state === 'overdue' && <span className="pl red">Overdue</span>}
        {r.state === 'late' && <span className="pl amb">Late</span>}
        <span className="mono xs">{whenLabel(r)}</span>
        {!mine && <button className="bt sm ghost" onClick={() => del.mutate()}>Cancel</button>}
      </div>
    </div>
  );
}

/** Add a reminder by hand: a centred dialog with the one Add-reminder form. */
function AddModal({ me, onClose }: { me: SessionUser; onClose: () => void }) {
  return <Modal label="Add a reminder" onClose={onClose}><AddReminderForm me={me} layout="modal" onSaved={onClose} onCancel={onClose} autoFocus /></Modal>;
}

export function RemindersPage({ me }: { me: SessionUser }) {
  return usePhone() ? <PhoneReminders me={me} /> : <DesktopReminders me={me} />;
}

function DesktopReminders({ me }: { me: SessionUser }) {
  const { list, groups } = useGroups();
  const others = useReminders('set_by_me');
  const [adding, setAdding] = useState(false);
  const open = (others.data ?? []).filter(r => !r.doneAt);
  return (
    <>
      <div className="hdr">
        <div className="htl"><h1>Reminders</h1><span className="xs mut">Add one yourself, or tell Ask AI in plain words. It shows here, on Today and in Notifications when it comes due.</span></div>
        <div className="hact"><AddButton onClick={() => setAdding(true)}>Add reminder</AddButton></div>
      </div>
      <div className="colsx rcols">
        <div className="l">
          {list.isLoading ? <Loading /> : groups.filter(g => g.rows.length || g.empty).map(g => (
            <section className="panel grp" key={g.k} aria-label={g.k}>
              <div className="sh" style={{ marginBottom: 4 }}><b>{g.k}</b><span className="mono">{g.rows.length}</span></div>
              {g.rows.length ? g.rows.map(r => <Row key={r.id} r={r} mine />) : <div className="xs mut" style={{ padding: '8px 0' }}>{g.empty}</div>}
            </section>
          ))}
        </div>
        <div className="r">
          <section className="panel grp" aria-label="Set by you for others">
            <div className="sh" style={{ marginBottom: 4 }}><b>Set by you for others</b><span className="mono">{open.length}</span></div>
            {open.map(r => <Row key={r.id} r={r} mine={false} />)}
            {!open.length && <div className="xs mut" style={{ padding: '8px 0', lineHeight: 1.6 }}>None. "Remind Sara Monday to send the matrix" sets one for Sara and tells her who set it.</div>}
          </section>
          <section className="panel" aria-label="How you get them">
            <div className="sh" style={{ marginBottom: 6 }}><b>How you get them</b></div>
            <div className="rhow">
              <div><b className="sm">In Notifications</b><span className="xs mut">when it comes due, highlighted until you dismiss it</span></div>
              <div><b className="sm">On your phone</b><span className="xs mut">a push at the time, when push is on</span></div>
              <div><b className="sm">On Today</b><span className="xs mut">the day's reminders, with a tick</span></div>
            </div>
            <div className="xs mut" style={{ lineHeight: 1.6, marginTop: 8 }}>A reminder with only a date comes due at 08:00 Riyadh time.</div>
            <Link className="bt" to="/settings" style={{ marginTop: 10, alignSelf: 'flex-start' }}>Notification settings</Link>
          </section>
        </div>
      </div>
      <AnimatePresence>{adding && <AddModal key="add" me={me} onClose={() => setAdding(false)} />}</AnimatePresence>
    </>
  );
}

function PhoneRow({ r }: { r: Reminder }) {
  const tick = useChange(() => (r.doneAt ? api.reminders.undo(r.id) : api.reminders.done(r.id)));
  const ai = r.setBy === 'assistant';
  const { who, forWhat } = subLine(r, true);
  const sub = [who, forWhat].filter(Boolean).join(' · ');
  return (
    <div className={`ph-rowx prm${r.doneAt ? ' done' : ''}`} role="listitem">
      <button type="button" className={`prchk${r.doneAt ? ' on' : ''}`} aria-label={r.doneAt ? 'Not done' : 'Done'} aria-pressed={!!r.doneAt} onClick={() => tick.mutate()} disabled={tick.isPending}><span><IconCheck /></span></button>
      <span className="ph-rt">
        <div className="t">{r.text}</div>
        {sub && <div className={`s${ai ? ' ai' : ''}`}>{ai && <span className="aiorb" aria-hidden="true" />}{sub}</div>}
      </span>
      <span className={`pwhen${r.state === 'overdue' ? ' red' : ''}`}>{r.state === 'overdue' ? 'Overdue' : whenLabel(r)}</span>
    </div>
  );
}

function PhoneReminders({ me }: { me: SessionUser }) {
  const { list, groups } = useGroups();
  const others = useReminders('set_by_me');
  const [adding, setAdding] = useState(false);
  const open = (others.data ?? []).filter(r => !r.doneAt);
  const shown = groups.filter(g => g.rows.length);
  return (
    <div className="ph-page ph-rem">
      <TopBar back={{ to: '/more', label: 'More' }} />
      <Title title="Reminders" sub="Say it the way you would to a colleague" />
      <SayBar phone />
      <div className="prem-add">
        <AddButton onClick={() => setAdding(true)}>Add reminder</AddButton>
        <span className="f13 mu">or say it above</span>
      </div>
      {list.isLoading ? <Loading /> : !shown.length ? <div className="ph-note">No reminders. Say one above, or Add reminder.</div> : shown.map(g => (
        <section key={g.k} aria-label={g.k}>
          <div className="ph-rgh"><b>{g.k}</b><span>{g.rows.length}</span></div>
          <div className="ph-grp" role="list">{g.rows.map(r => <PhoneRow key={r.id} r={r} />)}</div>
        </section>
      ))}
      {open.length > 0 && (
        <section aria-label="Set by you for others">
          <div className="ph-rgh"><b>Set by you for others</b><span>{open.length}</span></div>
          <div className="ph-grp" role="list">{open.map(r => (
            <div key={r.id} className="ph-rowx prm" role="listitem"><span className="ph-rt"><div className="t">{r.text}</div><div className="s">For {r.forUser.name}</div></span><span className="pwhen">{whenLabel(r)}</span></div>
          ))}</div>
        </section>
      )}
      <AnimatePresence>
        {adding && (
          <PSheet key="add" label="Add a reminder" onClose={() => setAdding(false)}>
            <h2>Add a reminder</h2>
            <AddReminderForm me={me} layout="modal" phone onSaved={() => setAdding(false)} onCancel={() => setAdding(false)} />
          </PSheet>
        )}
      </AnimatePresence>
    </div>
  );
}

const IconMic = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></svg>;
